import React, { useState, useEffect, useCallback } from 'react';
// import { useLanguage } from '../contexts/LanguageContext'; // Currently unused
import { useLocation, useNavigate } from 'react-router-dom';
import InterviewFeedbackModal from './InterviewFeedbackModal';
import InterviewSignInModal from './InterviewSignInModal';
import { fetchClientInterviews, submitInterviewFeedback } from '../services/apiService';

const Interviews = () => {
  console.log('[Interviews] Component is mounting/rendering');
  // const { selectedLanguage } = useLanguage(); // Currently unused
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('pending');
  const [pendingInterviews, setPendingInterviews] = useState([]);
  const [completedInterviews, setCompletedInterviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [hoveredCardId, setHoveredCardId] = useState(null);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [requiredEmailId, setRequiredEmailId] = useState(null);
  const [requiredMobileNumber, setRequiredMobileNumber] = useState(null);

  const isMobileView = window.innerWidth <= 480;
  const isTabletView = window.innerWidth <= 768;

  // Listen for auth success from InterviewSignInModal to hide modal immediately
  useEffect(() => {
    const onAuthSuccess = () => {
      console.log('[Interviews] Received interview-auth-success event');
      setIsAuthenticated(true);
      setShowSignInModal(false);
    };
    window.addEventListener('interview-auth-success', onAuthSuccess);
    return () => window.removeEventListener('interview-auth-success', onAuthSuccess);
  }, []);

  // Authentication check on component mount
  useEffect(() => {
    // Prefer router state, fallback to URL query params
    const stateEmailId = location.state?.emailId;
    const stateMobile = location.state?.mobileNumber;
    const qp = new URLSearchParams(location.search || '');
    const qEmail = qp.get('emailId');
    const qMobile = qp.get('mobileNumber');
    const qIsClient = qp.get('isClient') === 'true';
    const emailId = stateEmailId || qEmail;
    const mobileNumber = stateMobile || qMobile;

    // Check existing auth first; if authenticated, bypass modal regardless of params
    const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
    const sessionInterviewAuth = sessionStorage.getItem('interviewAuthenticated') === 'true';
    if (currentUser?.isAuthenticated || sessionInterviewAuth) {
      console.log('[Interviews] Existing authentication detected; opening interview screen directly');
      setIsAuthenticated(true);
      setShowSignInModal(false);
      return;
    }

    console.log('[Interviews] Checking authentication:', { emailId, mobileNumber });

    if (emailId && mobileNumber) {
      // Interview access with specific email required
      setRequiredEmailId(emailId);
      setRequiredMobileNumber(mobileNumber);

      const isLoggedIn = !!currentUser.isAuthenticated;

      // Relaxed: accept any authenticated session or interview session flag
      if (isLoggedIn || sessionInterviewAuth) {
        setIsAuthenticated(true);
        console.log('[Interviews] User authenticated for interview flow');
      } else {
        setShowSignInModal(true);
        console.log('[Interviews] User not authenticated, showing sign-in modal');
      }
    } else {
      // Missing params: if isClient=true or route accessed directly, keep user here and show sign-in modal
      if (qIsClient || location.pathname.includes('/interviewFeedbackScreen') || location.pathname.includes('/interviews')) {
        console.log('[Interviews] Missing params but isClient or interview route detected. Showing sign-in modal.');
        setShowSignInModal(true);
        // Ensure any previous auth state is cleared
        setIsAuthenticated(false);
      } else {
        console.log('[Interviews] Missing params and not interview route; redirecting to home');
        navigate('/', { replace: true });
      }
    }
  }, [location, navigate]);

  // Simplified back button prevention (only for unauthenticated users)
  useEffect(() => {
    // Only apply restrictions if user is not authenticated
    if (!isAuthenticated && showSignInModal) {
      const handlePopState = (e) => {
        // Simple back button prevention without blocking other interactions
        window.history.pushState(null, '', window.location.href);
      };

      // Add a few history entries to make back button less effective
      for (let i = 0; i < 3; i++) {
        window.history.pushState(null, '', window.location.href);
      }

      window.addEventListener('popstate', handlePopState);
      
      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [isAuthenticated, showSignInModal]);


  const loadInterviews = useCallback(async () => {
    if (!isAuthenticated) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const urlParams = new URLSearchParams(location.search);
      const rawMobile = urlParams.get('mobileNumber');
      const rawEmail = urlParams.get('emailId');
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');

      // Normalize params: treat 'null'/'undefined' as missing
      const normalizedEmail = (rawEmail && rawEmail !== 'null' && rawEmail !== 'undefined') ? rawEmail : null;
      const normalizedMobile = (rawMobile && rawMobile !== 'null' && rawMobile !== 'undefined') ? rawMobile : null;

      const emailId = normalizedEmail || requiredEmailId || currentUser?.email || '';
      const mobileNumber = normalizedMobile || requiredMobileNumber || currentUser?.mobileNumber || '';
      
      console.log('[Interviews] Loading interviews with params:', { mobileNumber, emailId });
      
      // Make both API calls concurrently to reduce total time
      const [pendingResponse, completedResponse] = await Promise.all([
        fetchClientInterviews(mobileNumber, emailId, false),
        fetchClientInterviews(mobileNumber, emailId, true)
      ]);
      
      if (pendingResponse.success) {
        setPendingInterviews(Array.isArray(pendingResponse.data) ? pendingResponse.data : []);
      } else {
        console.error('Failed to fetch pending interviews:', pendingResponse.error);
        setPendingInterviews([]);
      }
      
      if (completedResponse.success) {
        setCompletedInterviews(Array.isArray(completedResponse.data) ? completedResponse.data : []);
      } else {
        console.error('Failed to fetch completed interviews:', completedResponse.error);
        setCompletedInterviews([]);
      }
      
    } catch (err) {
      setError('Failed to load interviews');
      console.error('Error loading interviews:', err);
      setPendingInterviews([]);
      setCompletedInterviews([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, location.search, requiredMobileNumber, requiredEmailId]);

  useEffect(() => {
    // Load interviews only when authenticated
    if (isAuthenticated) {
      loadInterviews();
    }
  }, [isAuthenticated, loadInterviews]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleInterviewClick = (interview) => {
    console.log('Interview clicked:', interview);
    setSelectedInterview(interview);
    setIsFeedbackModalOpen(true);
  };

  const handleCloseFeedbackModal = () => {
    setIsFeedbackModalOpen(false);
    setSelectedInterview(null);
  };

  const handleSubmitFeedback = async (feedbackData) => {
    try {
      console.log('Submitting feedback:', feedbackData);
      
      // Submit feedback via API
      const response = await submitInterviewFeedback(
        selectedInterview.interviewId,
        feedbackData.candidateStatus,
        feedbackData.interviewFeedback,
        feedbackData.skillsRatings
      );

      if (response.success) {
        // Update the interview status based on feedback
        const updatedInterview = {
          ...selectedInterview,
          status: feedbackData.candidateStatus === 'Selected' ? 'Selected' : 
                  feedbackData.candidateStatus === 'Rejected' ? 'Rejected' : 'NoShow',
          feedback: feedbackData.interviewFeedback
        };

        // Move from pending to completed if it was pending
        if (activeTab === 'pending') {
          setPendingInterviews(prev => prev.filter(interview => 
            interview.interviewId !== selectedInterview.interviewId
          ));
          setCompletedInterviews(prev => [...prev, updatedInterview]);
        } else {
          // Update existing completed interview
          setCompletedInterviews(prev => 
            prev.map(interview => 
              interview.interviewId === selectedInterview.interviewId ? updatedInterview : interview
            )
          );
        }

        // Return success response to modal
        return response;
      } else {
        throw new Error(response.error || 'Failed to submit feedback');
      }
      
    } catch (error) {
      console.error('Error submitting feedback:', error);
      throw error; // Re-throw to let modal handle the error
    }
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'Scheduled': '#2563eb',
      'Confirmed': '#059669',
      'Pending Confirmation': '#d97706',
      'Selected': '#059669',
      'Not Selected': '#dc2626',
      'Rejected': '#dc2626',
      'Completed': '#6366f1'
    };
    return statusColors[status] || '#6b7280';
  };

  const getStatusBadgeStyle = (status) => ({
    backgroundColor: getStatusColor(status),
    color: 'white',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '500'
  });

  const renderInterviewCard = (interview) => {
    
    // Parse interview date and time from API format
    const parseDateTime = (dateTimeStr, status) => {
      // Check if date is invalid or in 0000-00-00 format AND status is "Approved" or "Pending"
      const isInvalidDate = !dateTimeStr || 
                           dateTimeStr.trim() === '' || 
                           dateTimeStr.startsWith('0000-00-00');
      
      if (isInvalidDate && (status === 'Approved' || status === 'Pending')) {
        return { date: 'Not scheduled', time: 'Not scheduled' };
      }
      
      if (!dateTimeStr) return { date: 'N/A', time: 'N/A' };
      
      try {
        // Handle format "2025-01-03 14:00:00.0"
        const [datePart, timePart] = dateTimeStr.split(' ');
        const date = new Date(datePart + 'T' + timePart);
        
        // Check if date is valid
        if (isNaN(date.getTime())) {
          return { date: 'N/A', time: 'N/A' };
        }
        
        return {
          date: date.toLocaleDateString('en-IN', { 
            day: 'numeric', 
            month: 'short', 
            year: 'numeric' 
          }),
          time: date.toLocaleTimeString('en-IN', { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: true 
          })
        };
      } catch (e) {
        return { date: 'N/A', time: 'N/A' };
      }
    };

    const { date, time } = parseDateTime(interview.interviewDateTime, interview.status);
    
    return (
      <div
        key={interview.interviewId || interview.id}
        onClick={() => handleInterviewClick(interview)}
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: window.innerWidth <= 768 ? '16px' : '20px',
          // marginBottom: '16px',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          border: '1px solid #e5e7eb',
          boxShadow: hoveredCardId === (interview.interviewId || interview.id) 
            ? '0 8px 25px rgba(0, 0, 0, 0.15)' 
            : '0 2px 8px rgba(0, 0, 0, 0.1)',
          transform: hoveredCardId === (interview.interviewId || interview.id) 
            ? 'translateY(-2px)' 
            : 'translateY(0)',
          position: 'relative'
        }}
        onMouseEnter={() => setHoveredCardId(interview.interviewId || interview.id)}
        onMouseLeave={() => setHoveredCardId(null)}
      >
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: window.innerWidth <= 768 ? 'flex-start' : 'flex-start',
        marginBottom: '16px',
        flexDirection: window.innerWidth <= 480 ? 'column' : 'row',
        gap: window.innerWidth <= 480 ? '12px' : '0'
      }}>
        <div>
          <h3 style={{ 
            margin: '0 0 4px 0', 
            fontSize: window.innerWidth <= 768 ? '16px' : '18px', 
            fontWeight: '600', 
            color: '#1f2937'
          }}>
            {interview.candidateName || 'N/A'}
          </h3>
          <p style={{ 
            margin: '0', 
            fontSize: window.innerWidth <= 768 ? '13px' : '14px', 
            color: '#6b7280'
          }}>
            <i className="fas fa-briefcase" style={{ marginRight: '6px', color: '#2563eb' }}></i>
            {interview.designation || 'N/A'}
          </p>
        </div>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: window.innerWidth <= 768 ? '8px' : '12px',
          alignSelf: window.innerWidth <= 480 ? 'flex-start' : 'center'
        }}>
          <span style={getStatusBadgeStyle(interview.status || 'Unknown')}>
            {interview.status === 'Scheduled' ? 'Pending' : (interview.status || 'Unknown')}
          </span>
          <i className="fas fa-chevron-right" style={{ color: '#9ca3af', fontSize: '14px' }}></i>
        </div>
      </div>


      {/* Location & Contact */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: window.innerWidth <= 480 ? '1fr' : '1fr 1fr', 
        gap: window.innerWidth <= 768 ? '12px' : '16px', 
        marginBottom: '16px'
      }}>
        <div>
          <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            City
          </p>
          <p style={{ margin: '0', fontSize: '14px', color: '#374151', fontWeight: '500' }}>
            <i className="fas fa-map-marker-alt" style={{ marginRight: '6px', color: '#2563eb' }}></i>
            {interview.city || 'N/A'}
          </p>
        </div>
        <div>
          <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Mobile Number
          </p>
          <p style={{ margin: '0', fontSize: '14px', color: '#374151', fontWeight: '500' }}>
            <i className="fas fa-phone" style={{ marginRight: '6px', color: '#2563eb' }}></i>
            {interview.candidateMobile || 'N/A'}
          </p>
        </div>
      </div>

      {/* Interview Details */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: window.innerWidth <= 480 ? '1fr' : '1fr 1fr', 
        gap: window.innerWidth <= 768 ? '12px' : '16px', 
        marginBottom: '16px'
      }}>
        <div>
          <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Interview Date
          </p>
          <p style={{ margin: '0', fontSize: '14px', color: '#374151', fontWeight: '500' }}>
            <i className="fas fa-calendar" style={{ marginRight: '6px', color: '#2563eb' }}></i>
            {date}
          </p>
        </div>
        <div>
          <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Time
          </p>
          <p style={{ margin: '0', fontSize: '14px', color: '#374151', fontWeight: '500' }}>
            <i className="fas fa-clock" style={{ marginRight: '6px', color: '#2563eb' }}></i>
            {time}
          </p>
        </div>
      </div>


      {/* Feedback (for completed interviews) */}
      {interview.feedback && (
        <div style={{ 
          backgroundColor: '#f9fafb', 
          padding: '12px', 
          borderRadius: '8px',
          borderLeft: '4px solid #2563eb'
        }}>
          <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Feedback
          </p>
          <p style={{ margin: '0', fontSize: '14px', color: '#374151', fontStyle: 'italic' }}>
            "{interview.feedback}"
          </p>
        </div>
      )}
    </div>
    );
  };

  const renderEmptyState = (type) => (
    <div style={{ 
      textAlign: 'center', 
      padding: '60px 40px',
      backgroundColor: 'white',
      borderRadius: '12px',
      border: '1px solid #e5e7eb',
      width: '100%',
      maxWidth: '600px'
    }}>
      <div style={{ 
        width: '80px', 
        height: '80px', 
        backgroundColor: '#f3f4f6', 
        borderRadius: '50%', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        margin: '0 auto 20px'
      }}>
        <i className="fas fa-calendar-times" style={{ fontSize: '32px', color: '#9ca3af' }}></i>
      </div>
      <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#374151' }}>
        No {type} Interviews
      </h3>
      <p style={{ margin: '0', fontSize: '14px', color: '#6b7280' }}>
        {type === 'pending' 
          ? 'You don\'t have any upcoming interviews scheduled.' 
          : 'No completed interviews to show.'}
      </p>
    </div>
  );

  // Don't render interview content if not authenticated
  if (!isAuthenticated && showSignInModal) {
    return (
      <div style={{ minHeight: '100vh', height: '100vh', backgroundColor: '#f9fafb', overflow: 'auto', margin: 0, padding: 0 }}>
        <div style={{ padding: '0', width: '100%', maxWidth: 'none', margin: 0 }}>
          {/* Header */}
          <div style={{ 
            background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #2563eb 100%)',
            borderRadius: '0',
            padding: window.innerWidth <= 768 ? '2rem 16px' : '2.5rem 24px',
            margin: '0',
            color: 'white',
            position: 'relative'
          }}>
            <div>
              <h1 style={{ 
                margin: '0 0 8px 0', 
                fontSize: window.innerWidth <= 768 ? '1.75rem' : '2.25rem', 
                fontWeight: '700',
                lineHeight: '1.2'
              }}>
                <i className="fas fa-calendar-check" style={{ 
                  marginRight: window.innerWidth <= 768 ? '8px' : '12px',
                  fontSize: window.innerWidth <= 768 ? '1.5rem' : '2.25rem'
                }}></i>
                Interview Management
              </h1>
              <p style={{ 
                margin: '0', 
                fontSize: window.innerWidth <= 768 ? '0.875rem' : '1rem', 
                opacity: '0.9',
                lineHeight: '1.4'
              }}>
                Please sign in to access your interview feedback
              </p>
            </div>
          </div>
          
          {/* Sign-in required message */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: window.innerWidth <= 768 ? '300px' : '400px',
            padding: window.innerWidth <= 480 ? '20px 16px' : window.innerWidth <= 768 ? '30px 20px' : '40px 20px'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: window.innerWidth <= 480 ? '8px' : '12px',
              padding: window.innerWidth <= 480 ? '24px' : window.innerWidth <= 768 ? '32px' : '40px',
              textAlign: 'center',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              maxWidth: window.innerWidth <= 480 ? '320px' : '400px',
              width: '100%'
            }}>
              <div style={{
                width: window.innerWidth <= 480 ? '60px' : '80px',
                height: window.innerWidth <= 480 ? '60px' : '80px',
                backgroundColor: '#dbeafe',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: window.innerWidth <= 480 ? '0 auto 16px' : '0 auto 20px'
              }}>
                <i className="fas fa-lock" style={{ 
                  fontSize: window.innerWidth <= 480 ? '24px' : '32px', 
                  color: '#2563eb' 
                }}></i>
              </div>
              <h3 style={{ 
                margin: '0 0 12px 0', 
                fontSize: window.innerWidth <= 480 ? '18px' : '20px', 
                color: '#1f2937',
                lineHeight: '1.3'
              }}>
                Authentication Required
              </h3>
              <p style={{ 
                margin: '0', 
                fontSize: window.innerWidth <= 480 ? '13px' : '14px', 
                color: '#6b7280', 
                lineHeight: '1.5' 
              }}>
                Please sign in with your email to access the interview feedback system.
              </p>
            </div>
          </div>
          
          <InterviewSignInModal
            isOpen={showSignInModal}
            onClose={() => {
              setShowSignInModal(false);
              navigate('/', { replace: true });
            }}
            emailId={requiredEmailId}
            mobileNumber={requiredMobileNumber}
          />
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', height: '100vh', backgroundColor: '#f9fafb', overflow: 'auto', margin: 0, padding: 0 }}>
      <div style={{ padding: '0', width: '100%', maxWidth: 'none', margin: 0 }}>
        {/* Header */}
        <div style={{ 
          background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #2563eb 100%)',
          borderRadius: '0',
          padding: window.innerWidth <= 768 ? '2rem 16px' : '2.5rem 24px',
          margin: '0',
          color: 'white',
          position: 'relative'
        }}>
          <div>
            <h1 style={{ 
              margin: '0 0 8px 0', 
              fontSize: window.innerWidth <= 768 ? '1.75rem' : '2.25rem', 
              fontWeight: '700',
              lineHeight: '1.2'
            }}>
              <i className="fas fa-calendar-check" style={{ 
                marginRight: window.innerWidth <= 768 ? '8px' : '12px',
                fontSize: window.innerWidth <= 768 ? '1.5rem' : '2.25rem'
              }}></i>
              Interview Management
            </h1>
            <p style={{ 
              margin: '0', 
              fontSize: window.innerWidth <= 768 ? '0.875rem' : '1rem', 
              opacity: '0.9',
              lineHeight: '1.4'
            }}>
              Manage candidate interviews and feedback
            </p>
          </div>
        </div>

        {/* Statistics Cards */}
        <div style={{ 
          display: isMobileView ? 'flex' : 'grid', 
          flexDirection: isMobileView ? 'row' : undefined,
          flexWrap: isMobileView ? 'nowrap' : undefined,
          gridTemplateColumns: isMobileView ? undefined : isTabletView ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(250px, 1fr))', 
          gap: isMobileView ? '12px' : isTabletView ? '16px' : '20px', 
          marginBottom: '0',
          padding: isMobileView ? '12px' : isTabletView ? '16px' : '24px'
        }}>
          <div style={{ 
            backgroundColor: 'white', 
            padding: isMobileView ? '16px' : isTabletView ? '20px' : '24px', 
            borderRadius: isMobileView ? '8px' : '12px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e5e7eb',
            flex: isMobileView ? '1 1 0' : undefined
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: isMobileView ? 'center' : 'space-between',
              gap: isMobileView ? '8px' : '12px',
              flexDirection: isMobileView ? 'column' : 'row',
              textAlign: isMobileView ? 'center' : 'left',
              minHeight: isMobileView ? '60px' : 'auto'
            }}>
              <div>
                <p style={{ 
                  margin: '0 0 4px 0', 
                  fontSize: isMobileView ? '11px' : '14px', 
                  color: '#6b7280', 
                  whiteSpace: 'nowrap',
                }}>{isMobileView ? 'Total' : 'Total Interviews'}</p>
                <p style={{ 
                  margin: '0', 
                  fontSize: isMobileView ? '20px' : isTabletView ? '24px' : '28px', 
                  fontWeight: '700', 
                  color: '#1f2937' 
                }}>
                  {pendingInterviews.length + completedInterviews.length}
                </p>
              </div>
              {!isMobileView && (
                <div style={{ 
                  width: '56px', 
                  height: '56px', 
                  backgroundColor: '#dbeafe', 
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <i className="fas fa-calendar" style={{ 
                    fontSize: '24px', 
                    color: '#2563eb' 
                  }}></i>
                </div>
              )}
            </div>
          </div>

          <div style={{ 
            backgroundColor: 'white', 
            padding: isMobileView ? '16px' : isTabletView ? '20px' : '24px', 
            borderRadius: isMobileView ? '8px' : '12px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e5e7eb',
            flex: isMobileView ? '1 1 0' : undefined
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: isMobileView ? 'center' : 'space-between',
              gap: isMobileView ? '8px' : '12px',
              flexDirection: isMobileView ? 'column' : 'row',
              textAlign: isMobileView ? 'center' : 'left',
              minHeight: isMobileView ? '60px' : 'auto'
            }}>
              <div>
                <p style={{ 
                  margin: '0 0 4px 0', 
                  fontSize: isMobileView ? '11px' : '14px',
                  whiteSpace: 'nowrap', 
                  color: '#6b7280' 
                }}>Pending</p>
                <p style={{ 
                  margin: '0', 
                  fontSize: isMobileView ? '20px' : isTabletView ? '24px' : '28px', 
                  fontWeight: '700', 
                  color: '#1f2937' 
                }}>
                  {pendingInterviews.length}
                </p>
              </div>
              {!isMobileView && (
                <div style={{ 
                  width: '56px', 
                  height: '56px', 
                  backgroundColor: '#fef3c7', 
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <i className="fas fa-clock" style={{ 
                    fontSize: '24px', 
                    color: '#d97706' 
                  }}></i>
                </div>
              )}
            </div>
          </div>

          <div style={{ 
            backgroundColor: 'white', 
            padding: isMobileView ? '16px' : isTabletView ? '20px' : '24px', 
            borderRadius: isMobileView ? '8px' : '12px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e5e7eb',
            flex: isMobileView ? '1 1 0' : undefined
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: isMobileView ? 'center' : 'space-between',
              gap: isMobileView ? '8px' : '12px',
              flexDirection: isMobileView ? 'column' : 'row',
              textAlign: isMobileView ? 'center' : 'left',
              minHeight: isMobileView ? '60px' : 'auto'
            }}>
              <div>
                <p style={{ 
                  margin: '0 0 4px 0', 
                  fontSize: isMobileView ? '11px' : '14px',
                  whiteSpace: 'nowrap', 
                  color: '#6b7280' 
                }}>Completed</p>
                <p style={{ 
                  margin: '0', 
                  fontSize: isMobileView ? '20px' : isTabletView ? '24px' : '28px', 
                  fontWeight: '700', 
                  color: '#1f2937' 
                }}>
                  {completedInterviews.length}
                </p>
              </div>
              {!isMobileView && (
                <div style={{ 
                  width: '56px', 
                  height: '56px', 
                  backgroundColor: '#d1fae5', 
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <i className="fas fa-check-circle" style={{ 
                    fontSize: '24px', 
                    color: '#059669' 
                  }}></i>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div style={{ 
          backgroundColor: 'white', 
          borderRadius: '12px',
          border: 'none',
          borderBottom: 'none',
          margin: window.innerWidth <= 768 ? '0 16px' : '0 24px'
        }}>
          <div style={{ 
            display: 'flex', 
            // borderBottom: '1px solid #e5e7eb',
            flexDirection: 'row'
          }}>
            <button
              onClick={() => handleTabChange('pending')}
              style={{
                flex: 1,
                padding: window.innerWidth <= 768 ? '16px 20px' : '20px 32px',
                border: 'none',
                backgroundColor: activeTab === 'pending' ? '#2563eb' : 'transparent',
                color: activeTab === 'pending' ? 'white' : '#6b7280',
                fontSize: window.innerWidth <= 768 ? '16px' : '18px',
                fontWeight: '600',
                cursor: 'pointer',
                borderRadius: window.innerWidth <= 480 
                  ? (activeTab === 'pending' ? '12px 0 0 12px' : '0')
                  : (activeTab === 'pending' ? '12px 0 0 12px' : '0'),
                transition: 'all 0.2s ease'
              }}
            >
              <i className="fas fa-clock" style={{ 
                marginRight: window.innerWidth <= 480 ? '8px' : '12px' 
              }}></i>
              {window.innerWidth <= 480 ? `Pending (${pendingInterviews.length})` : `Pending (${pendingInterviews.length})`}
            </button>
            <button
              onClick={() => handleTabChange('completed')}
              style={{
                flex: 1,
                padding: window.innerWidth <= 768 ? '16px 20px' : '20px 32px',
                border: 'none',
                backgroundColor: activeTab === 'completed' ? '#2563eb' : 'transparent',
                color: activeTab === 'completed' ? 'white' : '#6b7280',
                fontSize: window.innerWidth <= 768 ? '16px' : '18px',
                fontWeight: '600',
                cursor: 'pointer',
                borderRadius: window.innerWidth <= 480 
                  ? (activeTab === 'completed' ? '0 12px 12px 0' : '0')
                  : (activeTab === 'completed' ? '0 12px 12px 0' : '0'),
                transition: 'all 0.2s ease'
              }}
            >
              <i className="fas fa-check-circle" style={{ 
                marginRight: window.innerWidth <= 480 ? '8px' : '12px' 
              }}></i>
              {window.innerWidth <= 480 ? `Done (${completedInterviews.length})` : `Completed (${completedInterviews.length})`}
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div style={{ 
          // backgroundColor: 'white',
          borderRadius: '0',
          border: 'none',
          borderTop: 'none',
          padding: window.innerWidth <= 768 ? '16px 16px' : '24px 24px',
          minHeight: window.innerWidth <= 768 ? '300px' : '400px',
          margin: '0'
        }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '80px 20px' }}>
              <div style={{ 
                width: '48px', 
                height: '48px', 
                border: '4px solid #f3f4f6', 
                borderTop: '4px solid #2563eb',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 20px'
              }}></div>
              <p style={{ margin: '0', color: '#6b7280', fontSize: '16px' }}>Loading interviews...</p>
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: '80px 20px' }}>
              <div style={{ 
                width: '96px', 
                height: '96px', 
                backgroundColor: '#fee2e2', 
                borderRadius: '50%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                margin: '0 auto 24px'
              }}>
                <i className="fas fa-exclamation-triangle" style={{ fontSize: '40px', color: '#dc2626' }}></i>
              </div>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '20px', color: '#374151' }}>Error Loading Interviews</h3>
              <p style={{ margin: '0 0 20px 0', fontSize: '16px', color: '#6b7280' }}>{error}</p>
              <button
                onClick={loadInterviews}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Try Again
              </button>
            </div>
          ) : (
            <div>
              {activeTab === 'pending' ? (
                pendingInterviews.length > 0 ? (
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: window.innerWidth <= 768 
                      ? '1fr' 
                      : window.innerWidth <= 1024 
                        ? 'repeat(auto-fill, minmax(350px, 1fr))' 
                        : 'repeat(auto-fill, minmax(400px, 1fr))', 
                    gap: window.innerWidth <= 768 ? '16px' : '20px' 
                  }}>
                    {pendingInterviews.map(renderInterviewCard)}
                  </div>
                ) : (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    minHeight: '400px'
                  }}>
                    {renderEmptyState('pending')}
                  </div>
                )
              ) : (
                completedInterviews.length > 0 ? (
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: window.innerWidth <= 768 
                      ? '1fr' 
                      : window.innerWidth <= 1024 
                        ? 'repeat(auto-fill, minmax(350px, 1fr))' 
                        : 'repeat(auto-fill, minmax(400px, 1fr))', 
                    gap: window.innerWidth <= 768 ? '16px' : '20px' 
                  }}>
                    {completedInterviews.map(renderInterviewCard)}
                  </div>
                ) : (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    minHeight: '400px'
                  }}>
                    {renderEmptyState('completed')}
                  </div>
                )
              )}
            </div>
          )}
        </div>

        {/* Interview Feedback Modal */}
        <InterviewFeedbackModal
          isOpen={isFeedbackModalOpen}
          onClose={handleCloseFeedbackModal}
          interview={selectedInterview}
          onSubmitFeedback={handleSubmitFeedback}
        />
        
        <InterviewSignInModal
          isOpen={showSignInModal}
          onClose={() => {
            setShowSignInModal(false);
            // Redirect to home if user cancels sign-in
            navigate('/', { replace: true });
          }}
          emailId={requiredEmailId}
          mobileNumber={requiredMobileNumber}
        />
      </div>
    </div>
  );
};

export default Interviews;
